// Locale-aware primitives: which language the UI resolved to, and the text and
// number formatting that depends on it.
//
// Everything here exists because the JS defaults are wrong for Kazakh and fail
// SILENTLY. `localeCompare()` and `toLocaleString()` with no locale argument use
// the BROWSER's locale, not the app's — so a user reading the UI in Kazakh on an
// en-US browser gets English collation and English thousands separators, with no
// error and nothing in the console to notice.
import i18n from "../../lang/i18n";

/**
 * The language the UI actually resolved to.
 *
 * `resolvedLanguage`, never `language`: the latter returns the tag that was
 * REQUESTED. If that tag is unsupported — a stale "kz", say, which is plausible
 * because the Kazakh catalog lives in a `kz/` folder while the language code is
 * "kk" — i18next renders the fallback, but `language` still reports "kz". Intl
 * does not reject unknown tags, it quietly substitutes the browser default:
 * `Intl.DateTimeFormat.supportedLocalesOf(["kz"])` is `[]`, and
 * `new Intl.PluralRules("kz")` resolves to en-US.
 */
export const uiLocale = () => i18n.resolvedLanguage || "en";

// Collators are expensive to build and these run inside sort comparators, i.e.
// O(n log n) times per render. The cache is keyed by locale+options, so a
// language switch misses it and builds a new collator — nothing to invalidate.
const collators = new Map();
const collator = (numeric) => {
  const locale = uiLocale();
  const key = `${locale}:${numeric}`;
  let instance = collators.get(key);
  if (!instance) {
    instance = new Intl.Collator(locale, { numeric, sensitivity: "base" });
    collators.set(key, instance);
  }
  return instance;
};

/**
 * Compare two display strings for sorting, in the UI's language.
 *
 * Kazakh has nine letters outside the Russian alphabet (ә ғ қ ң ө ұ ү һ і), and
 * codepoint order scatters them: a default sort yields
 *   Алма, Ән, Бота, Іс, Қала, Өмір, Ұзақ, Ыбырай
 * where Kazakh alphabetical order is
 *   Алма, Ән, Бота, Қала, Өмір, Ұзақ, Ыбырай, Іс
 * — і belongs at the END, not in fourth place. Russian and Kazakh collators
 * genuinely disagree, so the locale has to be passed explicitly.
 */
export const compareText = (a, b) =>
  collator(false).compare(String(a ?? ""), String(b ?? ""));

/**
 * As compareText, but digit runs compare numerically: 5A, 5B, 10A, 10B, 11A
 * rather than the plain string order 10A, 10B, 11A, 5A, 5B which scatters the
 * grade blocks a school thinks in. Also right for room codes (LAB2 before LAB10).
 */
export const naturalCompare = (a, b) =>
  collator(true).compare(String(a ?? ""), String(b ?? ""));

/**
 * Does the JS runtime actually carry CLDR data for this language?
 *
 * Kazakh is the app's DEFAULT language and is exactly the case where the
 * answer can be no: a build of Chrome without full ICU has no `kk` data and
 * Intl does not error on that — it silently formats from the ROOT locale, so
 * every date renders "2026 M08 10", every relative time comes out English
 * ("yesterday") and numbers use "1,234,567.5". A root-locale short month is
 * the literal "M08", which is what this probe reads. Asked once per language.
 */
const cldrByLocale = new Map();
export const localeHasCldrData = (locale = uiLocale()) => {
  if (!cldrByLocale.has(locale)) {
    let ok = false;
    try {
      const month = new Intl.DateTimeFormat(locale, { month: "short" }).format(
        new Date(2020, 0, 15),
      );
      ok = !/^M\d\d$/.test(month);
    } catch {
      ok = false;
    }
    cldrByLocale.set(locale, ok);
  }
  return cldrByLocale.get(locale);
};

/**
 * Which locale to hand Intl.NumberFormat. Russian stands in for a Kazakh the
 * runtime has no data for: the two agree on both separators (non-breaking
 * space grouping, comma decimal), so the substitution is invisible — whereas
 * the root locale it would otherwise fall back to is visibly wrong.
 */
const numberLocale = () => {
  const locale = uiLocale();
  return localeHasCldrData(locale) ? locale : "ru";
};

/**
 * Format a number in the UI's language. Grouping separators differ: en uses
 * "1,234,567", ru and kk use a non-breaking space, "1 234 567" — and the
 * DECIMAL separator differs too, which is why callers that want fixed
 * precision must come through here rather than reach for toFixed().
 *
 * `options` is passed straight to Intl.NumberFormat, e.g.
 * `{ minimumFractionDigits: 2, maximumFractionDigits: 2 }`.
 */
export const formatNumber = (value, options) => {
  const n = Number(value);
  return Number.isFinite(n) ? n.toLocaleString(numberLocale(), options) : "—";
};
